import { ReactNode, useMemo } from 'react';
import dayjs from 'dayjs';
import { formatDate } from '@frontend/date';
import { Trans, useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { theme } from '@frontend/theme';
import { ConfirmationModal, InlineNotification, Modal, Text } from '@frontend/design-system';
import {
  findEventById,
  findGridEventById,
  type DropPayload,
} from '../../../hooks/scheduler-v3/use-appointment-drag-drop';
import { getTimeDiff } from '../../../utils/date-helpers';
import { GridCalendarEvent, GridAppointmentCalendarEvent, CalendarViewOffices } from '../types';
import { checkIsNonAttendedEventStatus } from '../utils';

// ---------------------------------------------------------------------------
// Helpers for CalendarViewOffices (legacy timeline view)
// ---------------------------------------------------------------------------

const findEntityName = (data: CalendarViewOffices, entityId: string) => {
  const entity = findTargetColumnEntity(data, entityId);
  if (!entity) return '';
  return entity.entityName || getFullName(entity);
};

const minutesToTimeString = (minutesSinceCalendarStart: number, calendarStartHour: number): string => {
  const totalMinutes = minutesSinceCalendarStart + calendarStartHour * 60;
  return dayjs().startOf('day').add(totalMinutes, 'minute').format('hh:mm A');
};

const formatDateTime = (calendarDate: string, time: string) => {
  return `${formatDate(calendarDate, 'MMM D, YYYY') || calendarDate}, ${time}`;
};

const findTargetColumnEntity = (data: CalendarViewOffices, targetColumnEntityId: string) => {
  if (!data) return undefined;
  for (const office of data) {
    const entity = office.columnEntities?.find((e) => e.entityId === targetColumnEntityId);
    if (entity) return entity;
  }
  return undefined;
};

const parseTimeToMinutes = (timeStr: string): number => {
  const time = dayjs(timeStr, ['h:mm A', 'hh:mm A', 'H:mm', 'HH:mm']);
  return time.isValid() ? time.diff(time.startOf('day'), 'minute') : 0;
};

const checkForOverlappingAppointments = (
  data: CalendarViewOffices,
  pendingDropPayload: DropPayload,
  calendarStartHour: number
): boolean => {
  const { eventId, targetColumnEntityId, newStartTimeMinutes } = pendingDropPayload;

  const draggedEvent = findEventById(data, eventId);
  if (!draggedEvent) return false;

  const durationMinutes = getTimeDiff(draggedEvent.startHour, draggedEvent.endHour, { format: 'hh:mm A' });

  // Calculate the new time range (in minutes from midnight)
  const newStartMinutesFromMidnight = newStartTimeMinutes + calendarStartHour * 60;
  const newEndMinutesFromMidnight = newStartMinutesFromMidnight + durationMinutes;

  const targetEntity = findTargetColumnEntity(data, targetColumnEntityId);

  if (!targetEntity?.events) {
    return false;
  }

  // Check for overlaps with other events
  return targetEntity.events.some((event) => {
    const isNonAttendedEvent = checkIsNonAttendedEventStatus(event.status);
    if (event.type === 'unavailable' || isNonAttendedEvent || event.eventId === eventId) return false;
    const eventStartMinutes = parseTimeToMinutes(event.startHour);
    const eventEndMinutes = parseTimeToMinutes(event.endHour);
    const overlaps = newStartMinutesFromMidnight < eventEndMinutes && eventStartMinutes < newEndMinutesFromMidnight;
    return overlaps;
  });
};

/**
 * Resolves the entity display name from any grid event that belongs to that entity.
 */
const findGridEntityName = (gridEvents: GridCalendarEvent[], entityId: string): string => {
  if (!entityId) return '';
  const event = gridEvents.find((e) => e.entityId === entityId);
  return event?.entityName ?? '';
};

/**
 * Checks for overlapping appointment events in the target column using GridCalendarEvent[].
 * `startHour` / `endHour` on GridCalendarEvent are stored in hh:mm A format.
 */
const checkForOverlappingAppointmentsInGrid = (
  gridEvents: GridCalendarEvent[],
  pendingDropPayload: DropPayload,
  calendarStartHour: number
): boolean => {
  const { eventId, targetColumnEntityId, newStartTimeMinutes } = pendingDropPayload;

  const draggedEvent = findGridEventById(gridEvents, eventId);
  if (!draggedEvent) return false;

  const durationMinutes = getTimeDiff(draggedEvent.startHour ?? '', draggedEvent.endHour ?? '', {
    format: 'hh:mm A',
  });

  const newStartMinutesFromMidnight = newStartTimeMinutes + calendarStartHour * 60;
  const newEndMinutesFromMidnight = newStartMinutesFromMidnight + durationMinutes;

  // Only check appointment events in the target column (skip non-appointment event types).
  const targetColumnEvents = gridEvents.filter(
    (e): e is GridAppointmentCalendarEvent => e.entityId === targetColumnEntityId && e.eventType === 'appointment'
  );

  return targetColumnEvents.some((event) => {
    const isNonAttendedEvent = checkIsNonAttendedEventStatus(event.eventStatus);
    if (isNonAttendedEvent || event.id === eventId) return false;
    const eventStartMinutes = parseTimeToMinutes(event.startHour ?? '');
    const eventEndMinutes = parseTimeToMinutes(event.endHour ?? '');
    return newStartMinutesFromMidnight < eventEndMinutes && eventStartMinutes < newEndMinutesFromMidnight;
  });
};

type MessageType =
  | 'move-appointment'
  | 'move-appointment-across-entities'
  | 'overlapping-appointment-warning'
  | 'overlapping-appointment-warning-across-entities'
  | 'unassigned-drop-error'
  | null;

type Props = {
  show: boolean;
  /** pendingDropPayload from the drag-drop state — the only field this modal actually needs */
  pendingDropPayload: DropPayload | null;
  onConfirm: () => void;
  onClose: () => void;
  /**
   * Legacy CalendarViewOffices data (timeline view).
   * Pass either `data` or `gridEvents`, not both.
   */
  data?: CalendarViewOffices;
  /**
   * Flat GridCalendarEvent[] from useCalendarGridV3Data (grid view).
   * Pass either `gridEvents` or `data`, not both.
   */
  gridEvents?: GridCalendarEvent[];
  calendarDate: string;
  startHour?: number;
};

export const AppointmentDragDropConfirmationModal = ({
  show,
  pendingDropPayload,
  onConfirm,
  onClose,
  data,
  gridEvents,
  calendarDate,
  startHour = 0,
}: Props) => {
  const { t } = useTranslation('scheduleCalendarViewMain');

  const messageType = useMemo((): MessageType => {
    if (!pendingDropPayload) return null;

    const { sourceColumnEntityId, targetColumnEntityId } = pendingDropPayload;

    if (targetColumnEntityId === '') {
      return 'unassigned-drop-error';
    }

    const isAcrossEntities = sourceColumnEntityId !== targetColumnEntityId;

    // Check if appointment already exist in target slot
    const hasOverlap = gridEvents
      ? checkForOverlappingAppointmentsInGrid(gridEvents, pendingDropPayload, startHour ?? 0)
      : data
      ? checkForOverlappingAppointments(data, pendingDropPayload, startHour ?? 0)
      : false;

    if (hasOverlap) {
      return isAcrossEntities ? 'overlapping-appointment-warning-across-entities' : 'overlapping-appointment-warning';
    }

    return isAcrossEntities ? 'move-appointment-across-entities' : 'move-appointment';
  }, [pendingDropPayload, data, gridEvents, startHour]);

  const { fromDateTime, toDateTime, fromProvider, toProvider } = useMemo(() => {
    if (!pendingDropPayload) {
      return { fromDateTime: '', toDateTime: '', fromProvider: '', toProvider: '' };
    }

    const { eventId, sourceColumnEntityId, targetColumnEntityId, newStartTimeMinutes } = pendingDropPayload;

    // Resolve the dragged event's original start time
    const originalStartTime = gridEvents
      ? findGridEventById(gridEvents, eventId)?.startHour ?? ''
      : findEventById(data, eventId)?.startHour ?? '';

    // Calculate new start time from drop position
    const newStartTime = minutesToTimeString(newStartTimeMinutes, startHour ?? 0);

    // Get entity names
    const sourceEntityName = gridEvents
      ? findGridEntityName(gridEvents, sourceColumnEntityId)
      : data
      ? findEntityName(data, sourceColumnEntityId)
      : '';
    const targetEntityName = gridEvents
      ? findGridEntityName(gridEvents, targetColumnEntityId)
      : data
      ? findEntityName(data, targetColumnEntityId)
      : '';

    return {
      fromDateTime: formatDateTime(calendarDate, originalStartTime),
      toDateTime: formatDateTime(calendarDate, newStartTime),
      fromProvider: sourceEntityName,
      toProvider: targetEntityName,
    };
  }, [pendingDropPayload, data, gridEvents, calendarDate, startHour]);

  const message = useMemo(() => {
    switch (messageType) {
      case 'unassigned-drop-error':
        return <Text>{t('This column does not allow for drag and drop appointments.')}</Text>;
      case 'move-appointment':
        return (
          <Text>
            <Trans t={t}>
              Are you sure you want to move this appointment from <strong>{{ fromDateTime }}</strong> to{' '}
              <strong>{{ toDateTime }}</strong>?
            </Trans>
          </Text>
        );
      case 'move-appointment-across-entities':
        return (
          <Text>
            <Trans t={t}>
              Are you sure you want to move this appointment from <strong>{{ fromDateTime }}</strong>, with{' '}
              <strong>{{ fromProvider }}</strong> to <strong>{{ toDateTime }}</strong>, with{' '}
              <strong>{{ toProvider }}</strong>?
            </Trans>
          </Text>
        );
      case 'overlapping-appointment-warning':
        return (
          <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
            <InlineWarningNotification type='overlappingAppointments' />
            <Text>
              <Trans t={t}>
                This appointment is overlapping another appointment, are you sure you want to move this appointment from{' '}
                <strong>{{ fromDateTime }}</strong> to <strong>{{ toDateTime }}</strong>?
              </Trans>
            </Text>
          </div>
        );
      case 'overlapping-appointment-warning-across-entities':
        return (
          <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
            <InlineWarningNotification type='overlappingAppointments' />
            <Text>
              <Trans t={t}>
                This appointment is overlapping another appointment, are you sure you want to move this appointment from{' '}
                <strong>{{ fromDateTime }}</strong>, with <strong>{{ fromProvider }}</strong> to{' '}
                <strong>{{ toDateTime }}</strong>, with <strong>{{ toProvider }}</strong>?
              </Trans>
            </Text>
          </div>
        );
      default:
        return null;
    }
  }, [t, messageType, fromDateTime, toDateTime, fromProvider, toProvider]);

  if (!message) return null;

  if (messageType === 'unassigned-drop-error') {
    return <InfoModal show={show} title={t('Unable to complete move')} message={message} onClose={onClose} />;
  }

  return (
    <ConfirmationModal
      show={show}
      title={t('Move appointment?')}
      message={message}
      onClose={onClose}
      onConfirm={onConfirm}
      confirmLabel={t('Move appointment')}
      cancelLabel={t('Cancel')}
    />
  );
};

type InlineWarningNotificationType = 'overlappingAppointments';

const InlineWarningNotification = ({ type }: { type: InlineWarningNotificationType }) => {
  const { t } = useTranslation('scheduleCalendarViewMain');

  const text = useMemo(() => {
    if (type === 'overlappingAppointments') {
      return t('Appointments are overlapping');
    }
    return '';
  }, [t, type]);

  if (!text) return null;

  return (
    <InlineNotification size='large' status='warn' css={inlineNotificationFullWidthStyles}>
      {text}
    </InlineNotification>
  );
};

const inlineNotificationFullWidthStyles = {
  '& > div': { width: '100%' },
};

type InfoModalProps = {
  show: boolean;
  title?: string;
  message?: ReactNode | null;
  trackingId?: string;
  onClose: () => void;
};

const InfoModal = ({ show, title, message, trackingId, onClose }: InfoModalProps) => {
  const { t } = useTranslation('scheduleCalendarViewMain');

  return (
    <Modal show={show} onClose={onClose} maxWidth={400}>
      <Modal.Header onClose={onClose}>{title}</Modal.Header>
      <Modal.Body>{message}</Modal.Body>
      <Modal.Footer
        secondary={{
          label: t('Close'),
          trackingId,
          onClick: onClose,
        }}
      />
    </Modal>
  );
};
